import React, { useMemo, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf/pdf.worker.min.js`;

const MIN_SCALE = 0.6;
const MAX_SCALE = 2.2;
const SCALE_STEP = 0.2;
const PDF_OPTIONS = {
  withCredentials: true,
};

const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const getHighlightedText = (text, searchTerm) => {
  if (!searchTerm) return text;

  const pattern = new RegExp(`(${escapeRegExp(searchTerm)})`, "gi");
  return String(text).replace(pattern, "<mark>$1</mark>");
};

const PdfQuestion = ({
  question,
  pdfPath,
  pdfFileName,
  showNextButton,
  onNext,
  showFinishButton,
  onFinish,
}) => {
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageTexts, setPageTexts] = useState([]);
  const [activeMatchIdx, setActiveMatchIdx] = useState(0);
  const [loadError, setLoadError] = useState("");
  const [downloading, setDownloading] = useState(false);

  const normalizedSearchTerm = searchTerm.trim().toLocaleLowerCase("uk-UA");
  const matches = useMemo(() => {
    if (!normalizedSearchTerm) return [];

    return pageTexts
      .map((text, idx) => ({
        pageNumber: idx + 1,
        text: String(text || "").toLocaleLowerCase("uk-UA"),
      }))
      .filter((page) => page.text.includes(normalizedSearchTerm));
  }, [normalizedSearchTerm, pageTexts]);

  const loadTextIndex = async (pdf) => {
    const texts = [];
    for (let pageIdx = 1; pageIdx <= pdf.numPages; pageIdx += 1) {
      const page = await pdf.getPage(pageIdx);
      const textContent = await page.getTextContent();
      texts.push(textContent.items.map((item) => item.str).join(" "));
    }
    setPageTexts(texts);
  };

  const onDocumentLoadSuccess = (pdf) => {
    setNumPages(pdf.numPages);
    setPageNumber(1);
    setActiveMatchIdx(0);
    setLoadError("");
    loadTextIndex(pdf);
  };

  const goToMatch = (nextIdx) => {
    if (!matches.length) return;

    const safeIdx = (nextIdx + matches.length) % matches.length;
    setActiveMatchIdx(safeIdx);
    setPageNumber(matches[safeIdx].pageNumber);
  };

  const goToPage = (nextPage) => {
    if (!numPages) return;
    setPageNumber(Math.min(numPages, Math.max(1, nextPage)));
  };

  const downloadPdf = async () => {
    if (!pdfPath || downloading) return;

    setDownloading(true);
    try {
      const response = await fetch(pdfPath, { credentials: "include" });
      if (!response.ok) {
        throw new Error("PDF download failed");
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = pdfFileName || "document.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      window.open(pdfPath, "_blank", "noreferrer");
    } finally {
      setDownloading(false);
    }
  };

  if (!pdfPath) {
    return (
      <div className="pdf-question-empty">
        PDF-файл не додано.
      </div>
    );
  }

  return (
    <div className="pdf-question">
      {question && (
        <div className="test-question question-title" dangerouslySetInnerHTML={{ __html: question }} />
      )}

      <div className="pdf-toolbar">
        <div className="pdf-toolbar-group">
          <button type="button" onClick={() => goToPage(pageNumber - 1)} disabled={pageNumber <= 1}>
            Назад
          </button>
          <span>
            {pageNumber} / {numPages || "-"}
          </span>
          <button type="button" onClick={() => goToPage(pageNumber + 1)} disabled={!numPages || pageNumber >= numPages}>
            Далі
          </button>
        </div>

        <div className="pdf-toolbar-group">
          <button type="button" onClick={() => setScale((value) => Math.max(MIN_SCALE, value - SCALE_STEP))}>
            -
          </button>
          <span>{Math.round(scale * 100)}%</span>
          <button type="button" onClick={() => setScale((value) => Math.min(MAX_SCALE, value + SCALE_STEP))}>
            +
          </button>
        </div>

        <div className="pdf-search">
          <input
            type="search"
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value);
              setActiveMatchIdx(0);
            }}
            placeholder="Пошук у PDF"
          />
          <button type="button" onClick={() => goToMatch(activeMatchIdx - 1)} disabled={!matches.length}>
            ↑
          </button>
          <button type="button" onClick={() => goToMatch(activeMatchIdx + 1)} disabled={!matches.length}>
            ↓
          </button>
          <span>{matches.length ? `${activeMatchIdx + 1}/${matches.length}` : "0"}</span>
        </div>

        <a className="pdf-open-link" href={pdfPath} target="_blank" rel="noreferrer">
          Відкрити повністю
        </a>

        <button type="button" className="pdf-download-link" onClick={downloadPdf} disabled={downloading}>
          {downloading ? "Завантаження..." : "Завантажити"}
        </button>
      </div>

      {loadError && <div className="pdf-question-empty">{loadError}</div>}

      <div className="pdf-stage">
        <Document
          file={pdfPath}
          options={PDF_OPTIONS}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={() => setLoadError("Не вдалося завантажити PDF.")}
          loading={<div className="pdf-question-empty">Завантаження PDF...</div>}
        >
          <Page
            pageNumber={pageNumber}
            scale={scale}
            customTextRenderer={({ str }) => getHighlightedText(str, searchTerm.trim())}
          />
        </Document>
      </div>

      <div className="pdf-actions">
        {showNextButton && (
          <button className="btn nextAnswer" onClick={onNext} style={{ display: "inline-block" }}>
            Наступне завдання
          </button>
        )}

        {showFinishButton && (
          <button className="btn nextAnswer" onClick={onFinish} style={{ display: "inline-block" }}>
            Завершити
          </button>
        )}
      </div>
    </div>
  );
};

export default PdfQuestion;
